import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';


const EmployeeListTable = ({
  employees,
  selectedEmployees,
  onSelectEmployees,
  onSort,
  sortConfig,
  onEdit,
  onDelete,
  newlyImportedIds = []
}) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      onSelectEmployees(employees?.map(emp => emp?.id));
    } else {
      onSelectEmployees([]);
    }
  };

  const handleSelectOne = (employeeId) => {
    if (selectedEmployees?.includes(employeeId)) {
      onSelectEmployees(selectedEmployees?.filter(id => id !== employeeId));
    } else {
      onSelectEmployees([...selectedEmployees, employeeId]);
    }
  };

  const toggleRowExpansion = (employeeId) => {
    if (expandedRows?.includes(employeeId)) {
      setExpandedRows(expandedRows?.filter(id => id !== employeeId));
    } else {
      setExpandedRows([...expandedRows, employeeId]);
    }
  };

  const getComplianceStatusColor = (status) => {
    switch (status) {
      case 'Excellent': return 'text-green-600 bg-green-50';
      case 'Good': return 'text-blue-600 bg-blue-50';
      case 'Warning': return 'text-yellow-600 bg-yellow-50';
      case 'Critical': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'text-green-600 bg-green-50';
      case 'On Leave': return 'text-yellow-600 bg-yellow-50';
      case 'Inactive': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' ? 
      <Icon name="ChevronUp" size={14} className="text-primary" /> : 
      <Icon name="ChevronDown" size={14} className="text-primary" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedEmployees?.length === employees?.length && employees?.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
              />
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => onSort('name')}
                className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-colors"
              >
                Employee
                <SortIcon columnKey="name" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => onSort('employeeId')}
                className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-colors"
              >
                Employee ID
                <SortIcon columnKey="employeeId" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => onSort('department')}
                className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-colors"
              >
                Department
                <SortIcon columnKey="department" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => onSort('role')}
                className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-colors"
              >
                Role
                <SortIcon columnKey="role" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => onSort('hireDate')}
                className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-colors"
              >
                Hire Date
                <SortIcon columnKey="hireDate" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <button
                onClick={() => onSort('status')}
                className="flex items-center gap-2 font-semibold text-sm text-foreground hover:text-primary transition-colors"
              >
                Status
                <SortIcon columnKey="status" />
              </button>
            </th>
            <th className="px-4 py-3 text-left">
              <span className="font-semibold text-sm text-foreground">Compliance</span>
            </th>
            <th className="px-4 py-3 text-right">
              <span className="font-semibold text-sm text-foreground">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {employees?.map((employee) => {
            const isNewlyImported = newlyImportedIds?.includes(employee?.id);
            return (
            <React.Fragment key={employee?.id}>
              <tr className={`hover:bg-muted/30 transition-colors ${isNewlyImported ? 'newly-imported-row' : ''}`}>
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedEmployees?.includes(employee?.id)}
                    onChange={() => handleSelectOne(employee?.id)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <AppImage
                      src={employee?.avatar}
                      alt={employee?.avatarAlt}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium text-foreground">{employee?.name}</div>
                      <div className="text-sm text-muted-foreground">{employee?.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-foreground">{employee?.employeeId}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{employee?.department}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{employee?.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{employee?.hireDate}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(employee?.status)}`}>
                    {employee?.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getComplianceStatusColor(employee?.complianceStatus)}`}>
                    {employee?.complianceStatus}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => toggleRowExpansion(employee?.id)}
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      title="View Details"
                    >
                      <Icon 
                        name={expandedRows?.includes(employee?.id) ? "ChevronUp" : "ChevronDown"} 
                        size={18} 
                        className="text-muted-foreground" 
                      />
                    </button>
                    <button
                      onClick={() => onEdit(employee)}
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      title="Edit"
                    >
                      <Icon name="Edit" size={18} className="text-muted-foreground hover:text-primary" />
                    </button>
                    <button
                      onClick={() => onDelete(employee?.id)}
                      className="p-1.5 hover:bg-muted rounded transition-colors"
                      title="Delete"
                    >
                      <Icon name="Trash2" size={18} className="text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                </td>
              </tr>
              {expandedRows?.includes(employee?.id) && (
                <tr className="bg-muted/20">
                  <td colSpan="9" className="px-4 py-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-semibold text-foreground">Phone:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.phone}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Email:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.email}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Department:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.department}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Role:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.role}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Job Title:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.jobTitle || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Employment Type:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.employmentType || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Manager:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.manager || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Work Location:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.workLocation || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Salary:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.salary ? `$${Number(employee?.salary)?.toLocaleString()}` : 'N/A'}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Hire Date:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.hireDate}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-foreground">Status:</span>
                        <span className="ml-2 text-muted-foreground">{employee?.status}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
          })}
        </tbody>
      </table>
      <style jsx>{`
        @keyframes highlightFade {
          0% {
            background-color: rgba(34, 197, 94, 0.3);
          }
          50% {
            background-color: rgba(34, 197, 94, 0.15);
          }
          100% {
            background-color: transparent;
          }
        }
        
        .newly-imported-row {
          animation: highlightFade 5s ease-in-out;
          border-left: 4px solid rgb(34, 197, 94);
        }
      `}</style>
      {employees?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No employees found</p>
        </div>
      )}
    </div>
  );
};

export default EmployeeListTable;